// Assignments Modal Component
// Displays all assignments for a specific template (read-only view)
// CHANGE: Created for viewing task assignments
// CHANGE: Transformed to consultation-only mode - removed edit and delete capabilities
// CHANGE: Simplified recurrence display with icons and tooltips
// CHANGE: Added sorting by next occurrence date
// CHANGE: Simplified to use nextOccurrenceDate from hook - removed local calculation

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Calendar, Clock, MapPin, User, Repeat, Info } from 'lucide-react';
import type { TemplateWithItems } from '../../lib/templates';
import { useAssignments } from '../../hooks/useAssignments';
import { formatRecurrenceText } from '../../lib/recurrence';
import { formatDuration, formatTimeToHHMM } from '../../lib/dateUtils';
import { CustomSelect } from '../ui/CustomSelect';
import { Tooltip } from '../ui/Tooltip';

interface AssignmentsModalProps {
  template: TemplateWithItems;
  onClose: () => void;
}

export function AssignmentsModal({ template, onClose }: AssignmentsModalProps) {
  const {
    assignments,
    loading,
    error,
    loadAssignments
  } = useAssignments(template.id);

  const [siteFilter, setSiteFilter] = useState<string>('all');

  useEffect(() => {
    loadAssignments();
  }, []);

  // Get unique sites for filter
  const sites = Array.from(new Set(assignments.map(a => a.site?.name).filter(Boolean)));

  // Filter assignments by site (sorting is already done in the hook)
  const filteredAssignments = useMemo(() => {
    return assignments.filter(assignment => {
      const matchesSite = siteFilter === 'all' || assignment.site?.name === siteFilter;
      return matchesSite;
    });
  }, [assignments, siteFilter]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-end" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative h-full w-full max-w-3xl bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Attributions - {template.name}
              </h2>
              <p className="text-sm text-gray-600">
                {filteredAssignments.length} attribution{filteredAssignments.length !== 1 ? 's' : ''}
                {siteFilter !== 'all' ? ' (filtrées)' : ''}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {sites.length > 0 && (
                <CustomSelect
                  value={siteFilter}
                  onChange={setSiteFilter}
                  options={[
                    { value: 'all', label: 'Tous les sites' },
                    ...sites.map(site => ({
                      value: site,
                      label: site
                    }))
                  ]}
                  placeholder="Filtrer par site"
                />
              )}

              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Chargement...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-2">Erreur lors du chargement</p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {siteFilter !== 'all'
                ? 'Aucune attribution ne correspond aux filtres'
                : 'Aucune attribution pour cette mission'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAssignments.map((assignment) => {
                const displayDate = assignment.nextOccurrenceDate;
                const isPastDate = displayDate && new Date(displayDate) < new Date(new Date().setHours(0, 0, 0, 0));

                return (
                  <div
                    key={assignment.id}
                    className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow ${isPastDate ? 'opacity-60' : ''}`}
                  >
                    <div className="space-y-2">
                      {/* User */}
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {assignment.user ? `${assignment.user.first_name} ${assignment.user.last_name}` : ''}
                        </span>
                        {assignment.is_recurring && (
                          <Tooltip
                            content={`${formatRecurrenceText(assignment.recurrence_rule)}${assignment.recurrence_end_date ? ` jusqu'au ${new Date(assignment.recurrence_end_date + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''}`}
                          >
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium cursor-help">
                              <Repeat className="w-3 h-3" />
                              Récurrent
                              <Info className="w-3 h-3" />
                            </span>
                          </Tooltip>
                        )}
                      </div>

                      {/* Site */}
                      {assignment.site && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{assignment.site.name}</span>
                        </div>
                      )}

                      {/* Date and Time */}
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {displayDate ? (
                            <span className={isPastDate ? 'text-gray-400' : ''}>
                              Prochaine: {new Date(displayDate + 'T00:00:00').toLocaleDateString('fr-FR', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          ) : (
                            <span className="text-gray-400">Non planifiée</span>
                          )}
                        </div>

                        {assignment.scheduled_time && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>{formatTimeToHHMM(assignment.scheduled_time)}</span>
                            {assignment.duration_override && (
                              <span className="text-gray-400">
                                ({formatDuration(assignment.duration_override)})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
}
